import React, { useCallback, useRef, useState } from 'react';
import { useResolvedMediaUrl } from '../hooks/useResolvedMediaUrl';
import { resolveMediaUrl } from '../utils/mediaApi';

export type SignedAudioProps = Omit<React.AudioHTMLAttributes<HTMLAudioElement>, 'src'> & {
  src: string;
};

const SignedAudio = React.forwardRef<HTMLAudioElement, SignedAudioProps>(({ src, ...rest }, ref) => {
  const [refreshToken, setRefreshToken] = useState(0);
  const lastRefreshAtRef = useRef(0);
  const resolved = useResolvedMediaUrl(src, refreshToken);

  const handleError = useCallback(async () => {
    if (!src || !src.startsWith('media/')) return;
    const now = Date.now();
    if (now - lastRefreshAtRef.current < 10_000) return; // throttle
    lastRefreshAtRef.current = now;

    try {
      await resolveMediaUrl(src, { bypassCache: true });
    } catch {
      // ignore
    }
    setRefreshToken((x) => x + 1);
  }, [src]);

  const chainedOnError = useCallback<React.ReactEventHandler<HTMLAudioElement>>(
    (e) => {
      try {
        const orig = (rest as any).onError as React.ReactEventHandler<HTMLAudioElement> | undefined;
        orig?.(e);
      } finally {
        handleError();
      }
    },
    [handleError, rest]
  );

  return <audio ref={ref} {...rest} src={resolved || ''} onError={chainedOnError} />;
});

SignedAudio.displayName = 'SignedAudio';

export default SignedAudio;
